import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';

/**
 * Wrapper robusto per ngx-spinner.
 *
 * Lo spinner viene gestito SOLO dall'HTTP interceptor (che passa 'app').
 * Le chiamate dai service (che passano 'body' o nessun nome) sono no-op:
 * erano ridondanti con l'interceptor e causavano doppio conteggio,
 * spinner bloccati e mismatch show/hide.
 *
 * Lo spinner visualizzato è sempre 'body' (posizionato nel full-layout
 * con CSS dedicato in styles.css per escludere header e sidebar).
 *
 * Safety timeout: se lo spinner resta visibile per più di 30s,
 * viene forzatamente chiuso (contatore leak, unsubscribe, ecc.).
 */
@Injectable({
	providedIn: 'root',
})
export class SpinnerService {
	private readonly SPINNER_NAME = 'body';
	private readonly SAFETY_TIMEOUT_MS = 30000;
	private readonly INTERCEPTOR_KEY = 'app';

	private spinnerCount = 0;
	private safetyTimer: ReturnType<typeof setTimeout> | null = null;
	/** finestra aperta da silenzioso(): le richieste HTTP avviate dentro non mostrano lo spinner e vengono contate */
	private traccia: { pending: number; onFine: () => void } | null = null;
	/** tabelle che stanno mostrando le righe skeleton ([kcsCaricamento]): intanto lo spinner globale non si vede */
	private soppressioni = 0;

	constructor(private spinnerService: NgxSpinnerService) {}

	/**
	 * Esegue fn senza spinner globale per le richieste HTTP che fn avvia (sottoscritte subito, come fanno le liste
	 * al cambio pagina) e chiama onFine quando sono tutte terminate, o subito se fn non ne avvia. Lo usa la tabella
	 * che mostra le proprie righe skeleton al posto dello spinner (TabellaSkeletonService).
	 */
	silenzioso(fn: () => void, onFine: () => void): void {
		const t = { pending: 0, onFine };
		const prev = this.traccia;
		this.traccia = t;
		try {
			fn();
		} catch (e) {
			// fn fallita: le richieste gia' partite chiudono da sole, ma senza nessuna in corso onFine non arriverebbe
			// piu' (tabella ferma sulle righe skeleton)
			if (t.pending === 0) onFine();
			throw e;
		} finally {
			this.traccia = prev;
		}
		if (t.pending === 0) onFine();
	}

	/** Per l'interceptor: se la richiesta parte dentro silenzioso() ritorna la funzione da chiamare a fine richiesta
	 *  (e lo spinner non va mostrato), altrimenti null */
	inizioSilenzioso(): (() => void) | null {
		const t = this.traccia;
		if (!t) return null;
		t.pending++;
		return () => {
			if (--t.pending === 0) t.onFine();
		};
	}

	/**
	 * Mostra lo spinner globale.
	 * Solo le chiamate dall'interceptor (spinnerName = 'app') hanno effetto.
	 * Le chiamate dai service (spinnerName = 'body' o omesso) sono no-op.
	 */
	show(spinnerName: string = 'body'): void {
		if (spinnerName !== this.INTERCEPTOR_KEY) return;
		this.spinnerCount++;
		if (this.spinnerCount === 1) {
			if (this.soppressioni === 0) this.spinnerService.show(this.SPINNER_NAME);
			this.startSafetyTimer();
		}
	}

	/** Una tabella mostra le sue righe skeleton: lo spinner globale si nasconde (anche se gia' visibile) finche' non
	 *  chiama riattiva(); il conteggio delle richieste continua normalmente */
	sopprimi(): void {
		if (++this.soppressioni === 1 && this.spinnerCount > 0) this.spinnerService.hide(this.SPINNER_NAME);
	}

	riattiva(): void {
		if (this.soppressioni === 0) return;
		if (--this.soppressioni === 0 && this.spinnerCount > 0) this.spinnerService.show(this.SPINNER_NAME);
	}

	/**
	 * Nasconde lo spinner globale (quando tutte le chiamate concorrenti sono terminate).
	 * Solo le chiamate dall'interceptor (spinnerName = 'app') hanno effetto.
	 */
	hide(spinnerName: string = 'body'): void {
		if (spinnerName !== this.INTERCEPTOR_KEY) return;
		if (this.spinnerCount <= 0) {
			this.spinnerCount = 0;
			return;
		}
		this.spinnerCount--;
		if (this.spinnerCount === 0) {
			this.spinnerService.hide(this.SPINNER_NAME);
			this.clearSafetyTimer();
		}
	}

	/** Forza la chiusura dello spinner e resetta il contatore. */
	forceHide(): void {
		this.spinnerCount = 0;
		this.spinnerService.hide(this.SPINNER_NAME);
		this.clearSafetyTimer();
	}

	private startSafetyTimer(): void {
		this.clearSafetyTimer();
		this.safetyTimer = setTimeout(() => {
			if (this.spinnerCount > 0) {
				console.warn(
					`[SpinnerService] Safety timeout: spinner bloccato da ${this.SAFETY_TIMEOUT_MS / 1000}s ` +
					`(contatore era ${this.spinnerCount}). Forzo la chiusura.`
				);
				this.forceHide();
			}
		}, this.SAFETY_TIMEOUT_MS);
	}

	private clearSafetyTimer(): void {
		if (this.safetyTimer !== null) {
			clearTimeout(this.safetyTimer);
			this.safetyTimer = null;
		}
	}
}
